import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../apiConfig';
import { ClipboardDocumentIcon, ArrowTopRightOnSquareIcon, InformationCircleIcon, PlayIcon, StopIcon } from '@heroicons/react/24/outline';

const Dashboard = ({ trades }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('All');
  const [profitLossFilter, setProfitLossFilter] = useState('All');
  const [tradingEnabled, setTradingEnabled] = useState(true);
  const [loadingTradingStatus, setLoadingTradingStatus] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showSellDetails, setShowSellDetails] = useState(false);
  const [tradingConfig, setTradingConfig] = useState(null);
  const [mockMode, setMockMode] = useState(false);
  const [loadingMockMode, setLoadingMockMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [solPrice, setSolPrice] = useState(null);
  const [loadingSolPrice, setLoadingSolPrice] = useState(true);
  const [tokenPrices, setTokenPrices] = useState({});
  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [loadingMonitoringStatus, setLoadingMonitoringStatus] = useState(false);

  // Use only real trades from backend - no sample data fallback
  const displayTrades = trades || [];

  // Debug logging for trades data
  console.log('Dashboard received trades:', trades);
  console.log('Trades type:', typeof trades);
  console.log('Trades length:', trades?.length);
  console.log('Trades is array:', Array.isArray(trades));
  console.log('Using display trades:', displayTrades);

  // Fetch SOL price from CoinGecko
  const fetchSolPrice = async () => {
    try {
      setLoadingSolPrice(true);
      const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      if (response.data.solana && response.data.solana.usd) {
        setSolPrice(response.data.solana.usd);
      } else {
        setSolPrice(null);
      }
    } catch (error) {
      console.error('Failed to fetch SOL price:', error);
      setSolPrice(null);
    } finally {
      setLoadingSolPrice(false);
    }
  };

  // Format USD value
  const formatUsdValue = (solAmount) => {
    if (!solAmount || !solPrice) return '$0.00';
    const usdValue = parseFloat(solAmount) * solPrice;
    return `$${usdValue.toFixed(2)}`;
  };

  // Fetch token price from DexScreener
  const fetchTokenPrice = async (tokenAddress) => {
    try {
      const response = await axios.get(`https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`);
      if (response.data && response.data.pairs && response.data.pairs.length > 0) {
        const pair = response.data.pairs[0];
        return parseFloat(pair.priceUsd) || 0;
      }
      return 0;
    } catch (error) {
      console.error(`Failed to fetch price for token ${tokenAddress}:`, error);
      return 0;
    }
  };

  // Fetch prices for all active trades
  const fetchTokenPrices = async () => {
    const activeTrades = displayTrades.filter(trade => 
      hasBuyTransactionIcon(trade) && !trade.sell_time && trade.token_address
    );
    
    const pricePromises = activeTrades.map(async (trade) => {
      const price = await fetchTokenPrice(trade.token_address);
      return { tokenAddress: trade.token_address, price };
    });
    
    try {
      const prices = await Promise.all(pricePromises);
      const priceMap = {};
      prices.forEach(({ tokenAddress, price }) => {
        priceMap[tokenAddress] = price;
      });
      setTokenPrices(priceMap);
    } catch (error) {
      console.error('Failed to fetch token prices:', error);
    }
  };

  // Fetch monitoring status
  const fetchMonitoringStatus = async () => {
    try {
      setLoadingMonitoringStatus(true);
      const apiUrl = getApiUrl();
      const response = await axios.get(`${apiUrl}/monitoring-status`);
      setMonitoringStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch monitoring status:', error);
      setMonitoringStatus(null);
    } finally {
      setLoadingMonitoringStatus(false);
    }
  };

  // Fetch trading status and config from backend
  const fetchTradingStatus = async () => {
    try {
      setLoadingTradingStatus(true);
      setLoadingMockMode(true);
      const apiUrl = getApiUrl();
      
      // Fetch trading status, config, and mock mode
      const [statusResponse, configResponse, mockModeResponse] = await Promise.all([
        axios.get(`${apiUrl}/trading-status`),
        axios.get(`${apiUrl}/trading-config`),
        axios.get(`${apiUrl}/mock-mode`)
      ]);
      
      setTradingEnabled(statusResponse.data.enabled);
      setTradingConfig(configResponse.data);
      setMockMode(mockModeResponse.data.mock_mode);
    } catch (error) {
      console.error('Failed to fetch trading status:', error);
    } finally {
      setLoadingTradingStatus(false);
      setLoadingMockMode(false);
    }
  };

  // Update trading status on backend
  const updateTradingStatus = async (enabled) => {
    try {
      const apiUrl = getApiUrl();
      await axios.post(`${apiUrl}/trading-status`, { enabled });
      setTradingEnabled(enabled);
      const status = enabled ? 'enabled' : 'disabled';
      alert(`Trading ${status} successfully!`);
    } catch (error) {
      console.error('Failed to update trading status:', error);
      alert('Failed to update trading status. Please try again.');
    }
  };

  // Update mock mode on backend
  const updateMockMode = async (mockModeEnabled) => {
    try {
      const apiUrl = getApiUrl();
      await axios.post(`${apiUrl}/mock-mode`, { mock_mode: mockModeEnabled });
      setMockMode(mockModeEnabled);
      const mode = mockModeEnabled ? 'mock' : 'live';
      alert(`Trading mode set to ${mode} successfully!`);
      
      // Refresh trading config to get updated status
      fetchTradingStatus();
    } catch (error) {
      console.error('Failed to update mock mode:', error);
      alert('Failed to update trading mode. Please try again.');
    }
  };

  // Calculate holding percentage
  const getHoldingPercentage = (trade) => {
    // If no buy transaction, return 0
    if (!trade.buy_time) return 0;
    
    // For tokens with buy transactions, default to 100% if amounts are not properly set
    if (!trade.original_amount || trade.original_amount <= 0) {
      // If we have a buy transaction but no original_amount, assume 100% holding
      return 100;
    }
    
    const currentAmount = trade.amount || trade.original_amount || 0;
    return Math.round((currentAmount / trade.original_amount) * 100);
  };

  // Helper function to calculate P/L with fallback to current price calculation
  const calculateProfitLoss = (trade) => {
    let profitLoss = trade.profit_loss;
    
    // For completed trades (with sell_time), always use the stored profit_loss
    if (trade.sell_time) {
      return profitLoss || 0;
    }
    
    // For active trades only: If profit_loss is 0, null, or undefined, calculate it from current price vs buy price
    if ((profitLoss === 0 || profitLoss === null || profitLoss === undefined) && 
        trade.buy_price && trade.buy_price > 0) {
      
      // Try to get real-time price from tokenPrices state
      const realTimePrice = tokenPrices[trade.token_address];
      
      // Use real-time price if available, otherwise use current_price, then buy_price as fallback
      const currentPrice = realTimePrice || trade.current_price || trade.buy_price;
      
      // Calculate P/L if we have a different price than buy price
      if (currentPrice !== trade.buy_price && currentPrice > 0) {
        profitLoss = ((currentPrice - trade.buy_price) / trade.buy_price) * 100;
      } else {
        // If prices are the same or invalid, P/L is 0
        profitLoss = 0;
      }
    }
    
    return profitLoss || 0;
  };

  // Fetch trading status on component mount
  useEffect(() => {
    fetchTradingStatus();
    fetchSolPrice(); // Fetch SOL price
    fetchMonitoringStatus(); // Fetch monitoring status
    
    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchTradingStatus();
      fetchSolPrice(); // Update SOL price every 5 seconds
      fetchMonitoringStatus(); // Update monitoring status every 5 seconds
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch token prices when trades change
  useEffect(() => {
    if (displayTrades.length > 0) {
      fetchTokenPrices();
    }
  }, [displayTrades]);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, profitLossFilter]);

  // Reset profit/loss filter when status filter changes
  useEffect(() => {
    if (statusFilter !== 'Completed') {
      setProfitLossFilter('All');
    }
  }, [statusFilter]);

  // Helper function to check if trade has buy transaction icon link
  const hasBuyTransactionIcon = (trade) => {
    return !!trade.transaction_id && !!trade.buy_time;
  };

  // Filter trades by status
  const filterByStatus = (trade) => {
    const hasBuy = !!trade.buy_time;
    const hasSell = !!trade.sell_time;
    const holdingPercent = getHoldingPercentage(trade);
    const hasTransactionIcon = hasBuyTransactionIcon(trade);
    
    switch (statusFilter) {
      case 'All':
        return true;
      case 'Active':
        // Active: has buy transaction icon link and no sell
        return hasTransactionIcon && !hasSell;
      case 'Completed':
        // Completed: has buy transaction icon but no holding (sold completely) OR has both buy and sell
        return hasTransactionIcon && (holdingPercent === 0 || hasSell);
      case 'Pending':
        // Pending: buy is processing (status = 'pending')
        return trade.status === 'pending';
      case 'Nothing':
        // Nothing: no buy transaction icon link
        return !hasTransactionIcon;
      case 'Failed':
        // Failed: trades that couldn't process buy transactions (failed status, slippage, no gas, etc.)
        return (trade.status === 'failed' || trade.failed) || 
               (hasBuy && !hasTransactionIcon) || // Buy attempted but no transaction ID
               (trade.transaction_warning && trade.transaction_warning.includes('failed'));
      default:
        return true;
    }
  };

  // Filter trades by profit/loss (only applies to completed trades)
  const filterByProfitLoss = (trade) => {
    if (profitLossFilter === 'All') return true;
    
    // Only apply profit/loss filter to completed trades
    const hasBuy = !!trade.buy_time;
    const hasSell = !!trade.sell_time;
    const holdingPercent = getHoldingPercentage(trade);
    const hasTransactionIcon = hasBuyTransactionIcon(trade);
    const isCompleted = hasTransactionIcon && (holdingPercent === 0 || hasSell);
    
    if (!isCompleted) return true; // Don't filter non-completed trades
    
    const profitLoss = calculateProfitLoss(trade);
    
    switch (profitLossFilter) {
      case 'Profit':
        return profitLoss > 0;
      case 'Loss':
        return profitLoss < 0;
      case 'Break Even':
        return profitLoss === 0;
      default:
        return true;
    }
  };

  // Get filtered and paginated trades
  const filteredTrades = displayTrades.filter(trade => filterByStatus(trade) && filterByProfitLoss(trade));
  const totalPages = Math.ceil(filteredTrades.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTrades = filteredTrades.slice(startIndex, startIndex + itemsPerPage);

  // Count trades by status
  const activeCount = displayTrades.filter(trade => hasBuyTransactionIcon(trade) && !trade.sell_time).length;
  const completedCount = displayTrades.filter(trade => hasBuyTransactionIcon(trade) && (getHoldingPercentage(trade) === 0 || trade.sell_time)).length;
  const pendingCount = displayTrades.filter(trade => trade.status === 'pending').length;
  const nothingCount = displayTrades.filter(trade => !hasBuyTransactionIcon(trade)).length;
  const failedCount = displayTrades.filter(trade => 
    (trade.status === 'failed' || trade.failed) || 
    (trade.buy_time && !hasBuyTransactionIcon(trade)) || // Buy attempted but no transaction ID
    (trade.transaction_warning && trade.transaction_warning.includes('failed'))
  ).length;

  // Get status badge with profit/loss info
  const getStatusBadge = (trade) => {
    if (trade.sell_time) {
      if (trade.profit_loss > 0) {
        return { text: 'Profit', class: 'bg-green-900/50 text-green-400' };
      } else if (trade.profit_loss < 0) {
        return { text: 'Loss', class: 'bg-red-900/50 text-red-400' };
      } else {
        return { text: 'Break Even', class: 'bg-gray-900/50 text-gray-400' };
      }
    } else if (trade.failed) {
      return { text: 'Failed', class: 'bg-red-900/50 text-red-400' };
    } else if (trade.buy_time) {
      return { text: 'Active', class: 'bg-purple-900/50 text-purple-400' };
    } else if (!trade.transaction_id) {
      return { text: 'Nothing', class: 'bg-gray-900/50 text-gray-400' };
    } else {
      return { text: 'Pending', class: 'bg-yellow-900/50 text-yellow-400' };
    }
  };

  // Get border color based on profit/loss
  const getBorderColor = (trade) => {
    if (trade.sell_time && trade.profit_loss !== undefined) {
      return trade.profit_loss > 0 
        ? 'border-green-500/50' 
        : trade.profit_loss < 0 
          ? 'border-red-500/50' 
          : 'border-slate-600';
    }
    return 'border-slate-600';
  };

  // Format address to first4...last4
  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (address.length <= 8) return address;
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  // Copy address to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Address copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert('Failed to copy address');
    }
  };

  // Format time to UTC+1
  const formatTimeUTC1 = (timeString) => {
    if (!timeString) return 'N/A';
    const date = new Date(timeString);
    // Add 1 hour for UTC+1
    date.setHours(date.getHours() + 1);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Generate Solscan URL for transaction
  const getSolanaExplorerUrl = (txHash) => {
    if (!txHash) return null;
    return `https://solscan.io/tx/${txHash}`;
  };

  // Open transaction in new tab
  const openTransactionLink = (txHash) => {
    const url = getSolanaExplorerUrl(txHash);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Show sell details popup
  const showSellDetailsPopup = (trade) => {
    setSelectedTrade(trade);
    setShowSellDetails(true);
  };

  // Close sell details popup
  const closeSellDetailsPopup = () => {
    setShowSellDetails(false);
    setSelectedTrade(null);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Debug Information */}
      <div className="card">
        <h4 className="text-primary-400 font-semibold mb-4 flex items-center">
          <InformationCircleIcon className="w-5 h-5 mr-2" />
          System Information
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-black/40 rounded-lg p-3 border border-primary-600/20">
            <p className="text-gray-400">Database Status</p>
            <p className="text-primary-400 font-medium">{trades ? 'Connected' : 'Disconnected'}</p>
          </div>
          <div className="bg-black/40 rounded-lg p-3 border border-primary-600/20">
            <p className="text-gray-400">Total Trades</p>
            <p className="text-primary-400 font-medium">{trades?.length || 0}</p>
          </div>
          <div className="bg-black/40 rounded-lg p-3 border border-primary-600/20">
            <p className="text-gray-400">Data Type</p>
            <p className="text-primary-400 font-medium">{Array.isArray(trades) ? 'Array' : typeof trades}</p>
          </div>
        </div>
      </div>

      {/* Trading Control Section */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div className="flex items-center space-x-4 mb-4 lg:mb-0">
            <h3 className="text-2xl font-bold" style={{ color: '#22c55e' }}>
              Trading Bot Control
            </h3>
            <div className={`px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border ${
              tradingEnabled 
                ? 'bg-primary-900/30 text-primary-400 border-primary-600/30' 
                : 'bg-red-900/30 text-red-400 border-red-600/30'
            }`}>
              {loadingTradingStatus ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-3 w-3 border border-transparent border-t-current"></div>
                  <span>Loading...</span>
                </div>
              ) : (tradingEnabled ? 'ENABLED' : 'DISABLED')}
            </div>
            {tradingConfig && (
              <div className={`px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border ${
                tradingConfig.real_trading_enabled 
                  ? 'bg-primary-900/30 text-primary-400 border-primary-600/30' 
                  : 'bg-yellow-900/30 text-yellow-400 border-yellow-600/30'
              }`}>
                {tradingConfig.trading_mode}
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <span className="text-sm text-slate-300">
              {tradingEnabled ? 'Bot is actively trading' : 'Bot is paused'}
            </span>
            <button
              onClick={() => updateTradingStatus(!tradingEnabled)}
              disabled={loadingTradingStatus}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                tradingEnabled ? 'bg-blue-600' : 'bg-slate-600'
              } ${loadingTradingStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  tradingEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        
        {/* Trading Actions */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between pt-4 border-t border-slate-600">
          <div className="flex flex-col space-y-3 mb-4 lg:mb-0 lg:flex-1">
            <div className="flex items-center space-x-4">
              <h4 className="text-md font-medium text-white">Trading Mode</h4>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                !mockMode 
                  ? 'bg-green-900/50 text-green-400' 
                  : 'bg-yellow-900/50 text-yellow-400'
              }`}>
                {loadingMockMode ? 'Loading...' : (!mockMode ? 'LIVE' : 'MOCK')}
              </div>
              {tradingConfig && !tradingConfig.private_key_configured && (
                <div className="px-2 py-1 rounded text-xs bg-red-900/50 text-red-400">
                  No Private Key
                </div>
              )}
              {/* Monitoring Status Indicator */}
              <button
                onClick={async () => {
                  // Refresh monitoring status first
                  await fetchMonitoringStatus();
                  
                  const status = monitoringStatus;
                  if (!status) {
                    alert('Failed to fetch monitoring status. Please try again.');
                    return;
                  }
                  
                  let message = `🔍 DETAILED MONITORING STATUS\n\n`;
                  message += `📊 System Status:\n`;
                  message += `• Active Trades Monitoring: ${status.monitoring_active ? '✅ Running' : '❌ Stopped'}\n`;
                  message += `• Trading Task: ${status.trading_task_active ? '✅ Running' : '❌ Stopped'}\n`;
                  message += `• Live Mode: ${status.live_mode_enabled ? '✅ Enabled' : '❌ Disabled'}\n\n`;
                  
                  message += `📈 Trading Data:\n`;
                  message += `• Active Trades: ${status.active_trades_count || 0}\n`;
                  message += `• Health Score: ${status.health_score || 'N/A'}%\n\n`;
                  
                  if (status.tasks_restarted && status.tasks_restarted.length > 0) {
                    message += `🔄 Recently Restarted:\n• ${status.tasks_restarted.join(', ')}\n\n`;
                  }
                  
                  if (status.last_update) {
                    message += `⏰ Last Update: ${new Date(status.last_update).toLocaleString()}\n`;
                  }
                  
                  message += `\n💡 Tip: Green = Active monitoring, Red = System offline`;
                  
                  alert(message);
                }}
                className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 hover:opacity-80 transition-opacity cursor-pointer ${
                  loadingMonitoringStatus 
                    ? 'bg-slate-700 text-slate-300' 
                    : monitoringStatus?.monitoring_active && monitoringStatus?.trading_task_active
                      ? 'bg-green-900/50 text-green-400 hover:bg-green-900/70'
                      : 'bg-red-900/50 text-red-400 hover:bg-red-900/70'
                }`}
                title="Click for detailed monitoring status"
              >
                <div className={`w-2 h-2 rounded-full ${
                  loadingMonitoringStatus
                    ? 'bg-slate-400 animate-pulse'
                    : monitoringStatus?.monitoring_active && monitoringStatus?.trading_task_active
                      ? 'bg-green-400 animate-pulse'
                      : 'bg-red-400'
                }`}></div>
                <span>
                  {loadingMonitoringStatus 
                    ? 'CHECKING...' 
                    : monitoringStatus?.monitoring_active && monitoringStatus?.trading_task_active
                      ? 'MONITORING'
                      : 'OFFLINE'
                  }
                </span>
              </button>
            </div>
            <div className="text-sm text-slate-400 max-w-md">
              {!mockMode ? 'Real transactions with actual SOL' : 'Mock transactions (no SOL spent)'}
              <br />
              <span className="text-xs">Click the monitoring indicator above to view system status and health.</span>
              {monitoringStatus && !monitoringStatus.monitoring_active && (
                <div className="mt-2 p-2 bg-red-900/20 border border-red-500/30 rounded text-xs text-red-300">
                  ⚠️ Automatic monitoring is offline. Use "Force Update" button to manually update prices.
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => updateMockMode(!mockMode)}
              disabled={loadingMockMode || !tradingConfig?.private_key_configured}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !mockMode 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-yellow-600 hover:bg-yellow-700 text-white'
              } ${(loadingMockMode || !tradingConfig?.private_key_configured) ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!tradingConfig?.private_key_configured ? 'Configure a valid private key to enable live trading' : ''}
            >
              {!mockMode ? (
                <>
                  <span>Live Mode</span>
                </>
              ) : (
                <>
                  <span>Mock Mode</span>
                </>
              )}
            </button>
            
            <button
              onClick={async () => {
                try {
                  const apiUrl = getApiUrl();
                  const response = await axios.post(`${apiUrl}/force-update-trades`);
                  const data = response.data;
                  
                  let message = `🔄 Force update completed!\n\n`;
                  message += `✅ Updated: ${data.updated_count} trades\n`;
                  message += `🎯 Sells triggered: ${data.sell_triggered_count}\n`;
                  message += `📊 Total trades: ${data.total_trades}\n`;
                  
                  if (data.sell_triggered_count > 0) {
                    message += `\n⚠️ ${data.sell_triggered_count} trades were sold due to stop loss or profit taking conditions.`;
                  }
                  
                  if (!monitoringStatus?.monitoring_active) {
                    message += `\n\n💡 Tip: Automatic monitoring is offline. You'll need to click this button periodically to update prices.`;
                  }
                  
                  alert(message);
                  fetchMonitoringStatus(); // Refresh monitoring status
                } catch (error) {
                  console.error('Failed to force update trades:', error);
                  alert('Failed to force update trades. Please try again.');
                }
              }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                monitoringStatus && !monitoringStatus.monitoring_active
                  ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
              }`}
              title={
                monitoringStatus && !monitoringStatus.monitoring_active
                  ? 'Automatic monitoring is offline - click to manually update prices'
                  : 'Manually trigger price updates for all active trades'
              }
            >
              <InformationCircleIcon className="w-4 h-4" />
              <span>
                {monitoringStatus && !monitoringStatus.monitoring_active ? 'Update Prices Now!' : 'Force Update'}
              </span>
            </button>
            
            <button
              onClick={async () => {
                try {
                  const apiUrl = getApiUrl();
                  const response = await axios.get(`${apiUrl}/monitoring-status`);
                  const status = response.data;
                  
                  let message = `Monitoring Status:\n`;
                  message += `• Active Trades Monitoring: ${status.monitoring_active ? '✅ Running' : '❌ Stopped'}\n`;
                  message += `• Trading Task: ${status.trading_task_active ? '✅ Running' : '❌ Stopped'}\n`;
                  message += `• Live Mode: ${status.live_mode_enabled ? '✅ Enabled' : '❌ Disabled'}\n`;
                  message += `• Active Trades: ${status.active_trades_count}\n`;
                  
                  if (status.tasks_restarted && status.tasks_restarted.length > 0) {
                    message += `\n🔄 Restarted tasks: ${status.tasks_restarted.join(', ')}`;
                  }
                  
                  alert(message);
                  
                  if (status.tasks_restarted && status.tasks_restarted.length > 0) {
                    fetchMonitoringStatus(); // Refresh if tasks were restarted
                  }
                } catch (error) {
                  console.error('Failed to check monitoring status:', error);
                  alert('Failed to check monitoring status. Please try again.');
                }
              }}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white transition-colors"
            >
              <InformationCircleIcon className="w-4 h-4" />
              <span>Check Status</span>
            </button>
          </div>
        </div>
      </div>

      {/* Trading Mode Warning Banner */}
      {(mockMode || displayTrades.some(trade => trade.is_mock_transaction) || (tradingConfig && !tradingConfig.real_trading_enabled)) && (
        <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="text-yellow-400 text-lg">⚠️</span>
            <div>
              <h4 className="text-yellow-400 font-semibold">
                {mockMode ? 'Mock Mode Active' : 
                 tradingConfig?.real_trading_enabled ? 'Mock Transactions Detected' : 'Development Mode Active'}
              </h4>
              <p className="text-yellow-300 text-sm">
                {mockMode ? 'Mock mode is enabled - no actual SOL is spent or tokens are bought.' :
                 tradingConfig?.real_trading_enabled 
                  ? 'Some trades show mock transaction IDs from previous testing.'
                  : 'The bot is running in MOCK mode - no actual SOL is spent or tokens are bought.'
                }
                {!tradingConfig?.real_trading_enabled && !mockMode && (
                  <span> To enable live trading, configure a real Solana private key in the backend environment.</span>
                )}
                {mockMode && tradingConfig?.private_key_configured && (
                  <span> Use the Trading Mode toggle above to switch to live trading.</span>
                )}
              </p>
              <p className="text-yellow-200 text-xs mt-1">
                {mockMode ? 'Mock mode can be toggled using the Trading Mode switch above.' :
                 tradingConfig?.real_trading_enabled 
                  ? 'New transactions will be real. Mock transactions are marked with "MOCK" badges.'
                  : 'Mock transactions are marked with "MOCK" badges and will not appear on Solana Explorer. See backend/README_TRADING.md for setup instructions.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trade Dashboard Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <h2 className="text-2xl font-bold text-white mb-4 lg:mb-0">Trade Dashboard</h2>
        
        {/* Status Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {['All', 'Active', 'Completed', 'Pending', 'Nothing', 'Failed'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setStatusFilter(status);
                setCurrentPage(1); // Reset to first page when filtering
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? 'text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
              style={statusFilter === status ? { backgroundColor: '#22c55e' } : {}}
            >
              {status} ({displayTrades.filter(trade => {
                const hasBuy = !!trade.buy_time;
                const hasSell = !!trade.sell_time;
                const holdingPercent = getHoldingPercentage(trade);
                const hasTransactionIcon = hasBuyTransactionIcon(trade);
                
                switch (status) {
                  case 'All': return true;
                  case 'Active': return hasTransactionIcon && !hasSell;
                  case 'Completed': return hasTransactionIcon && (holdingPercent === 0 || hasSell);
                  case 'Pending': return trade.status === 'pending';
                  case 'Nothing': return !hasTransactionIcon;
                  case 'Failed': return (trade.status === 'failed' || trade.failed) || 
                                        (hasBuy && !hasTransactionIcon) || 
                                        (trade.transaction_warning && trade.transaction_warning.includes('failed'));
                  default: return true;
                }
              }).length})
            </button>
          ))}
        </div>
      </div>

      {/* Profit/Loss Filter (only shown when Completed filter is selected) */}
      {statusFilter === 'Completed' && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm text-slate-400">Filter by P&L:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {['All', 'Profit', 'Loss', 'Break Even'].map((filter) => (
              <button
                key={filter}
                onClick={() => {
                  setProfitLossFilter(filter);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  profitLossFilter === filter
                    ? 'text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
                style={profitLossFilter === filter ? { backgroundColor: '#3b82f6' } : {}}
              >
                {filter} ({displayTrades.filter(trade => {
                  const hasBuy = !!trade.buy_time;
                  const hasSell = !!trade.sell_time;
                  const holdingPercent = getHoldingPercentage(trade);
                  const hasTransactionIcon = hasBuyTransactionIcon(trade);
                  const isCompleted = hasTransactionIcon && (holdingPercent === 0 || hasSell);
                  
                  if (!isCompleted) return false;
                  
                  const profitLoss = calculateProfitLoss(trade);
                  
                  switch (filter) {
                    case 'All': return true;
                    case 'Profit': return profitLoss > 0;
                    case 'Loss': return profitLoss < 0;
                    case 'Break Even': return profitLoss === 0;
                    default: return true;
                  }
                }).length})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Items Per Page Selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-slate-400">Items per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to first page
            }}
            className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        
        <span className="text-sm text-slate-400">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTrades.length)} of {filteredTrades.length} trades
        </span>
      </div>

      {/* Trades Table */}
      {filteredTrades.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-400 text-lg">No trades found for "{statusFilter}" filter</p>
          <p className="text-slate-500 text-sm mt-2">Try changing the filter or add some trades</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-600">
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Token</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Address</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Buy Price</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Sell Action</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Amount</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">P&L</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Buy Time</th>
                <th className="text-left py-3 px-4 text-slate-300 font-medium">Holding</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTrades.map((trade, index) => {
                const statusBadge = getStatusBadge(trade);
                const borderColor = getBorderColor(trade);
                const holdingPercentage = getHoldingPercentage(trade);
                
                return (
                  <tr 
                    key={trade.id || index} 
                    className={`border-b border-slate-700 hover:bg-slate-700/50 transition-colors ${borderColor}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-blue-400 font-medium">
                          {trade.symbol || trade.name || 'N/A'}
                        </span>
                        {/* Show appropriate status badge based on filter and trade status */}
                        {statusFilter === 'All' ? (
                          // In 'All' filter, show status badge for all tokens
                          <span className={`px-2 py-1 rounded-full text-xs ${statusBadge.class}`}>
                            {statusBadge.text}
                          </span>
                        ) : statusFilter === 'Active' && hasBuyTransactionIcon(trade) && !trade.sell_time ? (
                          // In 'Active' filter, show only one Active badge
                          <span className="px-2 py-1 rounded-full text-xs bg-purple-900/50 text-purple-400">
                            Active
                          </span>
                        ) : (
                          // For other cases, show the status badge from getStatusBadge
                          statusBadge.text !== 'Active' && (
                            <span className={`px-2 py-1 rounded-full text-xs ${statusBadge.class}`}>
                              {statusBadge.text}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-300 font-mono text-sm">
                          {formatAddress(trade.token_address)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(trade.token_address)}
                          className="text-slate-400 hover:text-blue-400 transition-colors"
                          title="Copy full address"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-green-400">
                          ${trade.buy_price || 'N/A'}
                        </span>
                        {trade.transaction_id && (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => openTransactionLink(trade.transaction_id)}
                              className="text-slate-400 hover:text-green-400 transition-colors"
                              title="View buy transaction on Solana Explorer"
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </button>
                            {trade.is_mock_transaction && (
                              <span 
                                className="text-xs bg-yellow-900/50 text-yellow-400 px-1 py-0.5 rounded"
                                title="Mock transaction - no actual blockchain activity"
                              >
                                MOCK
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => showSellDetailsPopup(trade)}
                          className="text-slate-400 hover:text-blue-400 transition-colors"
                          title="View sell details and actions"
                        >
                          <InformationCircleIcon className="h-4 w-4" />
                        </button>
                        <span className="text-xs text-slate-400">
                          {trade.partial_sales ? `${trade.partial_sales.length} sells` : 'No sells'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-300">
                      <div className="flex flex-col">
                        <span className="font-medium">{trade.amount || 'N/A'} SOL</span>
                        <span className="text-xs text-slate-400">
                          {loadingSolPrice ? 'Loading...' : formatUsdValue(trade.amount)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {(() => {
                        const profitLoss = calculateProfitLoss(trade);
                        if (profitLoss !== undefined && profitLoss !== null) {
                          return (
                            <span className={`font-medium ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {profitLoss >= 0 ? '+' : ''}{profitLoss.toFixed(2)}%
                            </span>
                          );
                        } else {
                          return <span className="text-slate-400">-</span>;
                        }
                      })()}
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-sm">
                      {formatTimeUTC1(trade.buy_time)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-slate-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              holdingPercentage > 50 ? 'bg-green-500' : 
                              holdingPercentage > 25 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ 
                              width: `${
                                statusFilter === 'Active' 
                                  ? (hasBuyTransactionIcon(trade) ? holdingPercentage : 0)
                                  : statusFilter === 'All'
                                    ? (hasBuyTransactionIcon(trade) ? holdingPercentage : 0)
                                    : holdingPercentage
                              }%` 
                            }}
                          />
                        </div>
                        <span className="text-xs text-slate-300 min-w-[3rem]">
                          {statusFilter === 'Active' 
                            ? (hasBuyTransactionIcon(trade) ? holdingPercentage : 0)
                            : statusFilter === 'All'
                              ? (hasBuyTransactionIcon(trade) ? holdingPercentage : 0)
                              : holdingPercentage}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sell Details Popup */}
      {showSellDetails && selectedTrade && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-600 rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                Sell Details - {selectedTrade.symbol || selectedTrade.name || 'Unknown Token'}
              </h3>
              <button
                onClick={closeSellDetailsPopup}
                className="text-slate-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Trade Summary */}
            <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-4 mb-4">
              <h4 className="text-lg font-semibold text-white mb-3">Trade Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Buy Amount:</span>
                  <div className="text-white font-medium">
                    {selectedTrade.original_amount || selectedTrade.amount || 0} SOL
                  </div>
                  <div className="text-slate-400 text-xs">
                    {loadingSolPrice ? 'Loading...' : formatUsdValue(selectedTrade.original_amount || selectedTrade.amount || 0)} USD
                  </div>
                </div>
                <div>
                  <span className="text-slate-400">Current Holding:</span>
                  <div className="text-white font-medium">
                    {selectedTrade.amount || 0} SOL
                  </div>
                  <div className="text-slate-400 text-xs">
                    {loadingSolPrice ? 'Loading...' : formatUsdValue(selectedTrade.amount || 0)} USD
                  </div>
                </div>
                <div>
                  <span className="text-slate-400">Buy Price:</span>
                  <div className="text-green-400 font-medium">
                    ${selectedTrade.buy_price || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-slate-400">Current Price:</span>
                  <div className="text-blue-400 font-medium">
                    ${selectedTrade.current_price || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Partial Sales History */}
            {selectedTrade.partial_sales && selectedTrade.partial_sales.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-white">Partial Sales History</h4>
                {selectedTrade.partial_sales.map((sale, index) => (
                  <div key={index} className="bg-slate-900/50 border border-slate-600 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-green-400 font-medium">
                        Sold {sale.amount} SOL
                      </span>
                      <span className="text-slate-400 text-sm">
                        {new Date(sale.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-400">Price:</span>
                        <div className="text-white">${sale.price}</div>
                      </div>
                      <div>
                        <span className="text-slate-400">Reason:</span>
                        <div className="text-white">{sale.reason}</div>
                      </div>
                      <div>
                        <span className="text-slate-400">P&L:</span>
                        <div className={`${sale.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {sale.profit_loss >= 0 ? '+' : ''}{sale.profit_loss.toFixed(2)}%
                        </div>
                      </div>
                      {sale.transaction_id && (
                        <div>
                          <span className="text-slate-400">Transaction:</span>
                          <button
                            onClick={() => openTransactionLink(sale.transaction_id)}
                            className="text-blue-400 hover:text-blue-300 text-xs"
                          >
                            View on Explorer
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-slate-400">No partial sales recorded yet</p>
              </div>
            )}

            {/* Holding Progress */}
            <div className="mt-4 bg-slate-900/50 border border-slate-600 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-white mb-3">Current Holding</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Holding Progress:</span>
                  <span className="text-white font-medium">{getHoldingPercentage(selectedTrade)}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-300 ${
                      getHoldingPercentage(selectedTrade) > 50 ? 'bg-green-500' : 
                      getHoldingPercentage(selectedTrade) > 25 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${getHoldingPercentage(selectedTrade)}%` }}
                  />
                </div>
                <div className="text-xs text-slate-400">
                  {selectedTrade.amount || 0} SOL remaining of {selectedTrade.original_amount || selectedTrade.amount || 0} SOL
                </div>
              </div>
            </div>

            {/* Price History */}
            {selectedTrade.price_history && selectedTrade.price_history.length > 0 ? (
              <div className="mt-4 bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-white mb-3">Price Movement History</h4>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {selectedTrade.price_history.slice().reverse().map((entry, index) => (
                    <div key={index} className="bg-slate-800/50 border border-slate-600 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-400 font-medium">
                            ${parseFloat(entry.price).toFixed(6)}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            entry.profit_loss >= 0 ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
                          }`}>
                            {entry.profit_loss >= 0 ? '+' : ''}{parseFloat(entry.profit_loss).toFixed(2)}%
                          </span>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {entry.volume_24h && (
                        <div className="text-xs text-slate-500">
                          Volume 24h: ${parseFloat(entry.volume_24h).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Showing {selectedTrade.price_history.length} price movements (newest first)
                </div>
              </div>
            ) : (
              <div className="mt-4 bg-slate-900/50 border border-slate-600 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-white mb-3">Price Movement History</h4>
                <div className="text-center py-4">
                  <p className="text-slate-400">No price history available yet</p>
                  <p className="text-slate-500 text-sm mt-1">Price movements will be tracked once monitoring starts</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Previous
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    currentPage === pageNum
                      ? 'text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                  style={currentPage === pageNum ? { backgroundColor: '#22c55e' } : {}}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
